import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

/**
 * Hook para verificar el estado de suscripción del usuario
 * Verifica en la tabla subscriptions (nueva tabla dedicada)
 * Campos: user_id, status, current_period_end
 */
export function useSubscription() {
  const { user, profile, loading: authLoading } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para verificar suscripción
  const checkSubscription = async () => {
    if (!user) {
      setLoading(false);
      return;
    }


    try {
      setLoading(true);
      setError(null);

      // Consultar la tabla subscriptions por user_id
      // Con retry automático para AbortErrors (conflictos de Web Locks)
      let data = null;
      let selectError = null;
      const MAX_RETRIES = 2;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const result = await supabase
          .from('subscriptions')
          .select('user_id, status, current_period_end')
          .eq('user_id', user.id)
          .maybeSingle();

        data = result.data;
        selectError = result.error;

        // Si no hay error o el error NO es AbortError, salir del retry loop
        if (!selectError || !selectError.message?.includes('AbortError')) {
          break;
        }

        // Si es AbortError y quedan reintentos, esperar y reintentar
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }

      if (selectError) {
        throw selectError;
      }

      // Si no hay suscripción, establecer valores por defecto
      if (!data) {
        setSubscriptionData({
          subscription_status: 'incomplete',
          status: 'incomplete',
          current_period_end: null,
          is_active: false
        });
        return;
      }

      // Verificar si está activa
      const status = data?.status;
      const periodEnd = data?.current_period_end;

      const isActive = (
        (status === 'active' || status === 'trialing') &&
        (!periodEnd || new Date(periodEnd) > new Date())
      );


      setSubscriptionData({
        subscription_status: status, // Mantener compatibilidad con código existente
        status: status,
        current_period_end: periodEnd,
        is_active: isActive
      });

    } catch (err) {
      setError(err);
      // En caso de error, asumir que no está suscrito
      setSubscriptionData({
        subscription_status: 'incomplete',
        status: 'incomplete',
        current_period_end: null,
        is_active: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Aún cargando auth — esperar
    if (authLoading) {
      return;
    }

    // Auth terminó pero no hay user — no hay suscripción que verificar
    if (!user) {
      setLoading(false);
      return;
    }

    // Solo ejecutar si tenemos user válido
    // Usar un timeout corto para evitar múltiples llamadas en re-renders rápidos
    const timeoutId = setTimeout(() => {
      checkSubscription();
    }, 100); // Pequeño delay para evitar llamadas múltiples

    return () => clearTimeout(timeoutId);
  }, [user?.id, authLoading]); // Depender solo de user.id

  // Verificar si la suscripción está activa
  // IMPORTANTE: La fuente de verdad es el campo 'status' de la tabla subscriptions
  const status = subscriptionData?.status || subscriptionData?.subscription_status;
  const isSubscribed = status === 'active' || status === 'trialing';

  // Log de depuración para verificar el cálculo
  if (subscriptionData) {
  }

  // Tipo de plan
  const planType = subscriptionData?.plan_type || null;

  return {
    isSubscribed,
    loading,
    planType,
    checkSubscription,
    subscriptionData,
    error
  };
}
